import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  TablePagination,
} from "@mui/material";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import ApiService from "../../modules/APIService.interceptors";
import ConfirmationModal from "../../components/ConfirmationModal/confirmationModal";
import CardMedia from "@mui/material/CardMedia";
import formatDate from "../../common/Format/FormatDate";
import { useAuthStore } from "../../store/zustand/authStore";
import NotAllowed from "../NotAllowed/NotAllowed";
import { CartContext } from "../../store/context/CartContext";

const Archives = () => {
  const [archivedLives, setArchivedLives] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [liveToUnarchive, setLiveToUnarchive] = useState(null);
  const [page, setPage] = useState(0); // 0-indexed (convención MUI)
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const user = useAuthStore((state) => state.user);
  const { setTitleSeccion } = useContext(CartContext);

  useEffect(() => {
    setTitleSeccion("Lives Archivados");
  }, []);


  useEffect(() => {
    getArchivedLives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const getArchivedLives = async () => {
    setLoader(true);
    try {
      // La API es 1-indexed, MUI 0-indexed -> mandamos page + 1
      const response = await ApiService.getArchivedLives(page + 1, pageSize);
      if (!response.error) {
        setArchivedLives(response.data);
        setTotal(response.total ?? 0);
      } else {
        console.error("Error al obtener los lives archivados");
      }
    } catch (error) {
      console.error("Error al obtener los lives archivados:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const unarchiveLive = async () => {
    setLoader(true);
    try {
      const response = await ApiService.unarchiveLive(liveToUnarchive.id); // Define esta ruta en tu ApiService
      if (!response.error) {
        setShowConfirmationModal(false);
        setLiveToUnarchive(null);

        // Si era el último item de la página y no es la primera, retrocedemos;
        // si no, refrescamos la página actual para mantener total/paginación.
        if (archivedLives.length === 1 && page > 0) {
          setPage((prev) => prev - 1);
        } else {
          getArchivedLives();
        }
      }
    } catch (error) {
      console.error("Error al desarchivar el live:", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <NotAllowed user={user} page={"LIVES"} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginTop: "1%",
          gap: "2%",
          minWidth: "50vh",
        }}
      >
        <Typography variant="h4" color="grey">
          Lives Archivados
        </Typography>

        {archivedLives?.length === 0 && total === 0 ? (
          <Typography variant="h6" color="GrayText">
            No hay lives archivados
          </Typography>
        ) : (
          <List dense>
            {archivedLives?.map((item) => (
              <ListItem
                key={item.id}
                sx={{ margin: "3% 0" }}
                secondaryAction={
                  user?.admin_users?.LIVES?.WRITE[0] ? (
                    <IconButton
                      edge="end"
                      aria-label="unarchive"
                      onClick={() => {
                        setShowConfirmationModal(true);
                        setLiveToUnarchive(item);
                      }}
                    >
                      <UnarchiveIcon color="primary" />
                    </IconButton>
                  ) : null // Si no tiene permisos, no renderiza nada
                }
              >
                <ListItemAvatar>
                  <Avatar>
                    <CardMedia
                      component="img"
                      height="auto"
                      image={item.thumbnail}
                      alt="Miniatura"
                    />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={item.title}
                  sx={{ color: "black" }}
                  secondary={formatDate(item.createdAt)}
                />
              </ListItem>
            ))}
          </List>
        )}

        {total > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={pageSize}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangePageSize}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Filas por página:"
            sx={{ color: "#767676" }}
          />
        )}
      </Box>

      {/* Backdrop de Carga */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      {/* Modal de Confirmación para Desarchivar */}
      <ConfirmationModal
        text={`¿Desarchivar el vivo "${liveToUnarchive?.title}"?`}
        textConfirmation={"Desarchivar"}
        textCancel={"Cancelar"}
        open={showConfirmationModal}
        showConfirmationButton={true}
        showCancelButton={true}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={unarchiveLive}
      />
    </>
  );
};

export default Archives;
